<!-- MediaLibraryPicker.vue – کامپوننت مستقل انتخاب فایل -->
<template>
  <div class="media-picker">
    <!-- Toolbar -->
    <div class="picker-toolbar">
      <b-input-group class="search-input">
        <b-input-group-prepend>
          <b-input-group-text><i class="fas fa-search"></i></b-input-group-text>
        </b-input-group-prepend>
        <b-form-input
            v-model="searchKeyword"
            placeholder="جستجو در فایل‌ها..."
            @keyup.enter="fetchFiles"
        />
      </b-input-group>

      <b-form-select
          v-model="filterMediaType"
          :options="mediaTypeOptions"
          size="sm"
          class="filter-select"
          @change="fetchFiles"
      />

      <b-form-select
          v-model="filterStatus"
          :options="statusOptions"
          size="sm"
          class="filter-select"
          @change="fetchFiles"
      />
      <span v-if="selectedIds.length > 0" class="selected-count">
        {{ selectedIds.length }} انتخاب شده
      </span>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">در حال بارگذاری...</span>
      </div>
    </div>

    <!-- Grid -->
    <div v-else-if="files.length > 0" class="picker-grid">
      <div
          v-for="file in files"
          :key="file.id"
          class="picker-item"
          :class="{ selected: isSelected(file.id) }"
          @click="toggleSelection(file.id)"
      >
        <div class="picker-thumb">
          <img
              v-if="getThumbnail(file)"
              :src="getThumbnail(file)"
              :alt="file.title || file.originalName"
              loading="lazy"
          />
          <feather-icon :icon="getFileIcon(file.mediaType)" size="30"></feather-icon>
          <div class="picker-check">
            <feather-icon icon="CheckCircleIcon"></feather-icon>
          </div>
        </div>
        <div class="picker-info">
          <div class="picker-title">{{ file.title || file.originalName }}</div>
          <div class="picker-meta">
            <span>{{ getMediaTypeLabel(file.mediaType) }}</span>
            <span>{{ formatSize(file.size) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty -->
    <div v-else class="text-center py-5">
      <i class="fas fa-folder-open" style="font-size: 4rem; color: #cbd5e1;"></i>
      <h4 class="mt-3">هیچ فایلی یافت نشد</h4>
      <p class="text-muted">با استفاده از دکمه آپلود، فایل جدید اضافه کنید.</p>
    </div>

    <!-- Pagination -->
    <b-pagination
        v-if="totalCount > 0 && totalCount>pageSize"
        v-model="pageIndex"
        :total-rows="totalCount"
        :per-page="pageSize"
        size="sm"
        class="mt-3"
        @change="fetchFiles"
    />
  </div>
</template>

<script>
import {
  BButton,
  BFormInput,
  BFormSelect,
  BInputGroup,
  BInputGroupPrepend,
  BInputGroupText,
  BPagination
} from 'bootstrap-vue'
import {MediaLibrarySearchRequest} from '@/libs/Api/MediaLibrary'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import Helper from '@/libs/Helper'

export default {
  name: 'MediaLibraryPicker',
  components: {
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupText,
    BFormInput,
    BFormSelect,
    BButton,
    BPagination
  },
  props: {
    multiple: {
      type: Boolean,
      default: false
    },
    mediaType: {
      type: Number | String,
      required: false,
      default: ''
    },
    initialSelectedIds: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      files: [],
      totalCount: 0,
      pageIndex: 1,
      pageSize: 20,
      searchKeyword: '',
      filterMediaType: '',
      filterStatus: '',
      selectedIds: [...this.initialSelectedIds],
      baseUrl: Helper.baseUrl || 'https://lenzavaapi.devsimagar.com',
      mediaTypeOptions: [
        { value: '', text: 'همه انواع' },
        { value: '1', text: 'تصویر' },
        { value: '2', text: 'ویدیو' },
        { value: '3', text: 'صوتی' },
        { value: '4', text: 'سند' }
      ],
      statusOptions: [
        { value: '', text: 'همه وضعیت‌ها' },
        { value: '0', text: 'در انتظار' },
        { value: '1', text: 'در حال پردازش' },
        { value: '2', text: 'پردازش شده' },
        { value: '3', text: 'ناموفق' }
      ]
    }
  },
  created() {
    this.fetchFiles()
  },
  watch: {
    mediaType: {
      immediate: true,
      async handler(val) {

        this.filterMediaType = val
      }
    }
  },
  methods: {
    fetchFiles() {
      this.loading = true
      const data = {
        Keyword: this.searchKeyword,
        MediaType: this.filterMediaType,
        Status: this.filterStatus,
        PageIndex: this.pageIndex - 1,
        PageSize: this.pageSize,
        SortBy: 'CreatedAt',
        SortDesc: true
      }
      const request = new MediaLibrarySearchRequest(this)
      request.setParams(data)
      request.fetch(
          (content) => {
            this.files = content.items || []
            this.totalCount = content.totalCount || 0
            this.loading = false
          },
          (error) => {
            this.loading = false
            this.$toast({
              component: ToastificationContent,
              position: 'bottom-center',
              props: {
                title: 'خطا',
                icon: 'XIcon',
                variant: 'danger',
                text: error.message || 'مشکل در بارگذاری فایل‌ها'
              }
            })
          }
      )
    },
    isSelected(fileId) {
      return this.selectedIds.includes(fileId)
    },
    toggleSelection(fileId) {
      if (this.multiple) {
        if (this.isSelected(fileId)) {
          this.selectedIds = this.selectedIds.filter(id => id !== fileId)
        } else {
          this.selectedIds = [...this.selectedIds, fileId]
        }
      } else {
        this.selectedIds = this.isSelected(fileId) ? [] : [fileId]
      }
    },
    getSelectedFiles() {
      return this.files.filter(f => this.selectedIds.includes(f.id))
    },
    getThumbnail(file) {
      if (!file?.thumbnailUrl) return null
      const url = file.thumbnailUrl
      if (url.startsWith('http://') || url.startsWith('https://')) return url
      const cleanUrl = url.startsWith('/') ? url.substring(1) : url
      return `${this.baseUrl}/${cleanUrl}`
    },
    getFileIcon(type) {
      const map = {1: 'CameraIcon', 2: 'VideoIcon', 3: 'MusicIcon', 4: 'FolderIcon'}
      return map[type] || 'fa-file'
    },
    getMediaTypeLabel(type) {
      const map = { 1: 'تصویر', 2: 'ویدیو', 3: 'صوتی', 4: 'سند' }
      return map[type] || 'ناشناخته'
    },
    formatSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    resetSelection() {
      this.selectedIds = []
    }
  }
}
</script>

<style scoped>
.media-picker {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
}

.picker-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
  align-items: center;
}

.search-input {
  flex: 1;
  min-width: 200px;
}

.filter-select {
  width: 130px;
}

.selected-count {
  font-size: 13px;
  color: #64748b;
  background: #f1f5f9;
  padding: 4px 14px;
  border-radius: 30px;
  font-weight: 600;
}

.picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 14px;
}

.picker-item {
  border-radius: 12px;
  border: 2px solid #e2e8f0;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fff;
  position: relative;
}

.picker-item:hover {
  border-color: #94a3b8;
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
}

.picker-item.selected {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.picker-item.selected .picker-check {
  opacity: 1;
}

.picker-thumb {
  width: 100%;
  aspect-ratio: 1/1;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  color: #94a3b8;
  position: relative;
  overflow: hidden;
}

.picker-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.picker-check {
  position: absolute;
  top: 8px;
  right: 8px;
  color: #6366f1;
  font-size: 22px;
  opacity: 0;
  transition: opacity 0.2s ease;
  text-shadow: 0 0 8px rgba(255, 255, 255, 0.8);
}

.picker-info {
  padding: 10px 12px 12px;
}

.picker-title {
  font-weight: 600;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.picker-meta {
  display: flex;
  gap: 8px;
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
}
</style>
